/**
 * Calculate totals from detail rows based on DetailTab columns
 */

export function calculateTotals(detailItems: BanHangRecord[] = []) {
  const t_so_luong = detailItems.reduce((sum, item) => sum + (Number(item.so_luong) || 0), 0);
  const t_khoi_luong = detailItems.reduce((sum, item) => sum + (Number(item.khoi_luong) || 0), 0);
  const t_thue_nt = detailItems.reduce((sum, item) => sum + (item.ct_km == '0' ? Number(item.thue_nt) || 0 : 0), 0);
  const t_ck_nt = detailItems.reduce((sum, item) => sum + (item.ct_km == '0' ? Number(item.ck_nt) || 0 : 0), 0);
  const t_km_nt = detailItems.reduce((sum, item) => sum + (item.ct_km == '1' ? Number(item.tien_nt2) || 0 : 0), 0);
  const t_tien_nt2 =
    detailItems.reduce((sum, item) => sum + (item.ct_km == '0' ? Number(item.tien_nt2) || 0 : 0), 0) - Number(t_ck_nt);
  const t_tc_tien_nt2 = detailItems.reduce((sum, item) => sum + (Number(item.tien_nt2) || 0), 0);
  const t_tt_nt = ((t_tien_nt2 + t_thue_nt) * 100) / 100;
  return {
    t_so_luong,
    t_khoi_luong,
    t_tien_nt2,
    t_thue_nt,
    t_km_nt,
    t_tc_tien_nt2,
    t_ck_nt,
    t_tt_nt
  };
}

export const getUnitWeight = (maVtData: BanHangRecord, selectedDvt: BanHangValue): number => {
  if (!maVtData) return 0;
  const baseDvt = maVtData.dvt;
  const targetDvt = selectedDvt || baseDvt;

  if (!targetDvt || targetDvt === baseDvt) {
    return Number(maVtData.khoi_luong) || 0;
  }

  const conversion = maVtData.quy_doi_dvt_data?.find(
    (item: BanHangRecord) =>
      item.dvt === targetDvt ||
      item.dvt_data?.uuid === targetDvt ||
      item.dvt_data?.dvt === targetDvt ||
      item.uuid === targetDvt
  );

  if (conversion) {
    if (conversion.khoi_luong !== null && conversion.khoi_luong !== undefined && conversion.khoi_luong !== '') {
      return Number(conversion.khoi_luong) || 0;
    }
    if (conversion.he_so !== null && conversion.he_so !== undefined && conversion.he_so !== '') {
      return (Number(conversion.he_so) || 0) * (Number(maVtData.khoi_luong) || 0);
    }
  }

  return Number(maVtData.khoi_luong) || 0;
};

export const calculateRowKhoiLuong = (
  soLuong: number | string | undefined | null,
  maVtData: BanHangRecord,
  selectedDvt: BanHangValue
): number => {
  const qty = Number(soLuong) || 0;
  const unitWeight = getUnitWeight(maVtData, selectedDvt);
  return qty * unitWeight;
};

export const handleUpdateRowFields = (row: BanHangRecord, field: string, newValue: BanHangValue) => {
  if (field === 'khoi_luong') {
    return { khoi_luong: Number(newValue) || 0 };
  }

  if (field === 'dvt') {
    const khoi_luong = calculateRowKhoiLuong(row.so_luong, row.ma_vt_data, newValue);
    return {
      dvt: newValue,
      khoi_luong
    };
  }

  if (
    field === 'so_luong' ||
    field === 'gia_nt2' ||
    field === 'tl_ck' ||
    field === 'ma_thue_data' ||
    field === 'gia_nt1' ||
    field === 'ct_km'
  ) {
    const so_luong = field === 'so_luong' ? newValue : row.so_luong;
    const gia_nt2 = field === 'gia_nt2' ? newValue : row.gia_nt2;
    const tl_ck = field === 'tl_ck' ? newValue : row.tl_ck;
    const thue_suat = field === 'ma_thue_data' ? newValue?.thue_suat || 0 : row.ma_thue_data?.thue_suat || 0;
    const gia_nt1 = field === 'gia_nt1' ? newValue : row.gia_nt1;
    const tien_nt2 = (Number(so_luong) || 0) * (Number(gia_nt2) || 0);
    const ck_nt = (tien_nt2 * (Number(tl_ck) || 0)) / 100;
    const thue_nt = ((tien_nt2 - ck_nt) * (Number(thue_suat) || 0)) / 100;
    const t_tc = (Number(so_luong) || 0) * (Number(gia_nt2) || 0);
    const khoi_luong = calculateRowKhoiLuong(so_luong, row.ma_vt_data, row.dvt);
    return {
      tien_nt2,
      ck_nt,
      gia_nt1,
      thue_nt,
      t_tc,
      khoi_luong
    };
  }

  if (field === 'ma_vt_data') {
    const so_luong = row.so_luong;
    const gia_nt2 = row.gia_nt2;
    const tl_ck = row.tl_ck;
    const thue_suat = row.ma_thue_data?.thue_suat || 0;
    const gia_nt1 = newValue?.gia_ban ?? row.gia_nt1;
    const tien_nt2 = (Number(so_luong) || 0) * (Number(gia_nt2) || 0);
    const ck_nt = (tien_nt2 * (Number(tl_ck) || 0)) / 100;
    const thue_nt = ((tien_nt2 - ck_nt) * (Number(thue_suat) || 0)) / 100;
    const t_tc = (Number(so_luong) || 0) * (Number(gia_nt2) || 0);
    const selectedDvt = newValue?.dvt_nl || newValue?.dvt_data?.uuid || newValue?.dvt || row.dvt;
    const khoi_luong = calculateRowKhoiLuong(so_luong, newValue, selectedDvt);

    return {
      dvt: selectedDvt,
      ma_kho_data: newValue?.ma_kho_data || row.ma_kho_data,
      tk_tl_data: newValue?.tk_tl_data || row.tk_tl_data,
      tk_dt_data: newValue?.tk_dt_data || row.tk_dt_data,
      tk_gv_data: newValue?.tk_gv_data || row.tk_gv_data,
      tk_km_data: newValue?.tk_km_data || row.tk_km_data,
      tk_ck_data: newValue?.tk_ck_data || row.tk_ck_data,
      tk_khcp_data: newValue?.tk_khcp_data || row.tk_khcp_data,
      tien_nt2,
      ck_nt,
      gia_nt1,
      thue_nt,
      t_tc,
      khoi_luong
    };
  }
  return {};
};
